import {ReportViewType, TimeAccountEntryType, TimeNavigationType} from "@/shared/timrTypes";
import {isStringEnumType} from "./utils/string";
import {getISODate, parseISODate, Temporal} from "@/shared/utils/datefunctions";

type ParserFn<T = unknown> = (raw: string | null) => T | undefined;
type ParserMap = globalThis.Record<string, ParserFn>;

type InferObject<M extends ParserMap> = { [K in keyof M]: ReturnType<M[K]> };

type AnchorList = readonly string[];

export function serializeQueryParamValue(value: unknown): string | undefined {
	if (value == null) {
		return undefined;
	}
	if (Array.isArray(value)) {
		return value.join(",");
	}
	if (value instanceof Temporal.PlainDate || value instanceof Temporal.ZonedDateTime) {
		return getISODate(value);
	}
	// eslint-disable-next-line @typescript-eslint/no-base-to-string
	return String(value);
}

/**
 * Helper class for type safe query parameter handling.
 * It allows to define a set of query parameters with their types and provides methods to get
 * and parse these parameters from the URL.
 * It also supports predefined anchors that can be used to navigate to specific parts of the application or that define
 * actions that should be performed when the page is loaded.
 */
export class QueryParamHelper<M extends ParserMap, A extends AnchorList = []> {
	private readonly map: M;
	private readonly anchorSet: Set<string>;

	constructor(builder: (t: typeof ParamTypes) => M, anchorValues: A = [] as unknown as A) {
		this.map = builder(ParamTypes);
		this.anchorSet = new Set(anchorValues);
	}

	/**
	 * Returns all possible query params
	 */
	public keys<K extends keyof M>(): K[] {
		return Object.keys(this.map) as K[];
	}

	/**
	 * Gets and parses the query parameters from the current URL.
	 * @param key The key of the parameter to get.
	 * @param search The search string to parse, defaults to `window.location.search`.
	 */
	public get<K extends keyof M>(key: K, search: string | URLSearchParams = window.location.search): ReturnType<M[K]> {
		const sp = typeof search === 'string' ? new URLSearchParams(search) : search;
		return this.map[key](sp.get(key as string)) as ReturnType<M[K]>;
	}

	/**
	 * Checks if a query parameter exists and is of correct type.
	 * @param key The key of the parameter to check.
	 * @param search The search string to parse, defaults to `window.location.search`.
	 */
	public has<K extends keyof M>(key: K, search: string | URLSearchParams = window.location.search): boolean {
		const sp = typeof search === 'string' ? new URLSearchParams(search) : search;
		return sp.has(key as string) && this.get(key, sp) !== undefined;
	}

	/**
	 * Gets the current anchor part of the URL, if it is one of the predefined anchor values.
	 * @param loc The location or URL to check, defaults to `window.location`.
	 */
	public getAnchor(loc: Location | URL = window.location): A[number] | undefined {
		const hash = (loc.hash ?? '').replace(/^#/, '');
		return this.anchorSet.has(hash) ? hash : undefined;
	}

	/**
	 * Clear the anchor part of current window location.
	 */
	public clearAnchor() {
		const url = new URL(window.location.href);
		url.hash = '';
		window.history.replaceState({}, '', url.toString());
	}

	/**
	 * Builds a link with the given query parameters and anchor.
	 * @param patch The parameters to patch into the URL.
	 * @param newPath Optional new path to set in the URL, defaults to current path.
	 * @param anchor Optional anchor to set in the URL, must be one of the predefined anchor values or `null` to clear it.
	 * @param base Optional base URL to use, defaults to `window.location`.
	 */
	public buildLink(
		patch: Partial<InferObject<M>>,
		newPath?: string,
		anchor?: A[number] | null,
		base: Location | URL = window.location
	): string {
		const url = new URL(base.href);

		if (newPath) {
			if (!newPath.startsWith('/')) {
				newPath = '/' + newPath;
			}
			if (!newPath.startsWith('/timr')) {
				newPath = '/timr' + newPath;
			}
			url.pathname = newPath;
		}

		const sp = url.searchParams;

		// DEVNOTE: We can extend the ParamTypes definitions to also support custom serialization functions
		// for the values, so that we can use more complex types like Date or Big
		// Int, but for now we just use the default string conversion.
		(Object.entries(patch) as [keyof M, unknown][]).forEach(([k, v]) => {
			const serializedValue = serializeQueryParamValue(v);
			if (serializedValue === undefined) {
				sp.delete(k as string);
			} else if (Array.isArray(v)) {
				sp.set(k as string, v.join(','));
			} else if (v instanceof Temporal.PlainDate || v instanceof Temporal.ZonedDateTime) {
				sp.set(k as string, getISODate(v));
			} else {
				sp.set(k as string, serializedValue);
			}
		});

		if (anchor === null) {                 // explicit clear
			url.hash = '';
		} else if (anchor !== undefined) {     // explicit set
			url.hash = '#' + anchor;
		} else {                               // keep current only if valid
			const cur = url.hash.replace(/^#/, '');
			if (!this.anchorSet.has(cur)) url.hash = '';
		}

		return url.toString();
	}
}

// Basic set of parser functions:
const parseNumber = (v: string | null) =>
	v == null ? undefined : (n => isFinite(n) ? n : undefined)(+v);

const parseEnum = <T extends globalThis.Record<string, string | number>>(type: T): (v: string | null) => T[keyof T] | undefined => {
	// Detect if it's a numeric enum by checking if any values are numbers
	const enumValues = Object.values(type);
	const isNumericEnum = enumValues.some(val => typeof val === "number");

	if (isNumericEnum) {
		return (v: string | null) => {
			if (v == null) return undefined;
			const numVal = Number(v);
			const numericValues = enumValues.filter(val => typeof val === "number") as T[keyof T][];
			return numericValues.includes(numVal as T[keyof T]) ? numVal as T[keyof T] : undefined;
		};
	} else {
		return (v: string | null) => isStringEnumType(v, type as globalThis.Record<string, string>) ? v as T[keyof T] : undefined;
	}
};

const parseArray = (v: string | null) => v ? v.split(',').filter(Boolean) : [];

const parseBoolean = (v: string | null) => v != null ? /^(1|true)$/i.test(v) : undefined;

/**
 * Helper object that defines the types of query parameters that can be used in the application.
 */
export const ParamTypes = {
	number: parseNumber,
	string: (v: string | null) => v ?? undefined,
	boolean: parseBoolean,
	array: parseArray,
	enum: parseEnum,
	date: parseISODate
} as const;

// Normally the definition should be located besides the page that uses the params, but since we can't import and export
// anything in our current build system it would require that the file that uses the params to generate a link would have to
// import the whole file, which is not ideal.
export const TimeAccountDashboardQueryParams = new QueryParamHelper(t => ({
	userFilter: t.number,
	actionFilter: t.enum(TimeAccountEntryType),
	showInactiveUsers: t.boolean
}), ["initializeTimeAccount", "exitUser"] as const);

export const BreakModelQueryParams = new QueryParamHelper(() => ({}), ["addBreakModel"] as const);

/** Shared reporting filters (base keys used across reporting UIs) */
const reportingBaseParsers = (t: typeof ParamTypes) => ({
	dateFrom: t.date,
	dateTo: t.date,
	timeNavigationType: t.enum(TimeNavigationType),
	userId: t.number,
	groupId: t.number,
	description: t.string,
	date: t.date,
	viewType: t.enum(ReportViewType)
});

export const ReportingQueryParams = new QueryParamHelper(t => ({
	...reportingBaseParsers(t)
}));

export const WorkingTimeReportingQueryParams = new QueryParamHelper(t => ({
	...reportingBaseParsers(t),
	description: t.string,
	workingTimeStatuses: t.string,
	workingTimeTypeId: t.string,
}));

export const ProjectTimeReportingQueryParams = new QueryParamHelper(t => ({
	...reportingBaseParsers(t),
	description: t.string,
	projectTimeStatuses: t.string,
	taskId: t.number,
	billable: t.boolean,
	customTaskField1: t.string,
	customTaskField2: t.string,
	customTaskField3: t.string,
}));

export const DriveLogReportingQueryParams = new QueryParamHelper(t => ({
	...reportingBaseParsers(t),
	carId: t.number,
	category: t.string,
	routePurposeVisited: t.string,
	driveLogStatuses: t.string,
}));
